/**
 * Auth state + auth endpoints.
 *
 * The access token lives in module-local memory (never localStorage) so an XSS
 * payload can't lift it out of `window`. The refresh token is delivered by the
 * backend as an httpOnly cookie, which we send via `credentials: "include"` in
 * `apiRequest`.
 *
 * Authoritative signatures: see backend/ENGINEERING_CONTRACT.md §"src/lib/auth.ts".
 */

import { apiRequest } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlatformRole = 'admin' | 'operator' | 'analyst' | 'readonly';

export type AccessClaims = {
  sub: string;
  role: PlatformRole;
  iat: number;
  exp: number;
};

export type MeOut = {
  id: string;
  email: string;
  full_name: string | null;
  platform_role: PlatformRole;
  totp_enabled: boolean;
  last_login_at: string | null;
};

export type LoginInput = {
  email: string;
  password: string;
  totp_code?: string;
};

export type TokenResponse = {
  access_token: string;
  expires_in: number;
};

export type TotpSetupResponse = {
  secret: string;
  otpauth_url: string;
  qr_svg: string | null;
};

// ---------------------------------------------------------------------------
// Token store with subscribers
// ---------------------------------------------------------------------------

let _token: string | null = null;
type Listener = (token: string | null) => void;
const _listeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return _token;
}

export function setAccessToken(t: string | null): void {
  if (_token === t) return;
  _token = t;
  for (const fn of _listeners) {
    try {
      fn(t);
    } catch {
      // Listener errors must not break other subscribers.
    }
  }
}

/**
 * Subscribe to access-token changes. Returns the unsubscribe handle. Used by
 * the auth context / `RequireAuth` so UI re-renders when the token flips.
 */
export function subscribeAccessToken(fn: Listener): () => void {
  _listeners.add(fn);
  return () => {
    _listeners.delete(fn);
  };
}

// ---------------------------------------------------------------------------
// JWT decode (header.payload.signature → payload only, no signature verify)
// ---------------------------------------------------------------------------

/**
 * Decode the payload of a JWT. The backend that issued the token is the
 * source of trust; we only decode here to read non-sensitive claims like
 * `exp` and `role` so the SPA can short-circuit obvious cases.
 */
export function decodeJwtClaims(token: string): AccessClaims | null {
  const parts = token.split('.');
  const payload = parts[1];
  if (parts.length !== 3 || payload === undefined) return null;
  try {
    // base64url → base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    const parsed = JSON.parse(json) as Partial<AccessClaims>;
    if (
      typeof parsed.sub === 'string' &&
      typeof parsed.role === 'string' &&
      typeof parsed.iat === 'number' &&
      typeof parsed.exp === 'number'
    ) {
      return parsed as AccessClaims;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/auth/login. Stores the access token on success. The refresh
 * token is set as an httpOnly cookie by the backend.
 *
 * For admin/operator without TOTP enrolment the backend returns 401 with a
 * `problem.type` that the LoginScreen surfaces; once TOTP is supplied the
 * second submit succeeds.
 */
export async function login(input: LoginInput): Promise<TokenResponse> {
  const res = await apiRequest<TokenResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
    // Login itself doesn't need refresh-on-401 retry; we want the 401 to
    // bubble so the UI can prompt for TOTP / show the wrong-creds message.
    skipAuthRefresh: true,
  });
  setAccessToken(res.access_token);
  return res;
}

/**
 * POST /api/v1/auth/refresh. The httpOnly refresh cookie travels via
 * `credentials: "include"`. Returns `null` (and clears the token) when the
 * refresh cookie is missing/expired so callers can route to /login.
 */
export async function refresh(): Promise<TokenResponse | null> {
  try {
    const res = await apiRequest<TokenResponse>('/api/v1/auth/refresh', {
      method: 'POST',
      // Don't recurse into refresh-on-401 from inside the refresh call itself.
      skipAuthRefresh: true,
    });
    setAccessToken(res.access_token);
    return res;
  } catch {
    // Any failure (401 expired cookie, network glitch, server down) clears
    // local state. Callers see `null` and route the user to /login.
    setAccessToken(null);
    return null;
  }
}

/**
 * POST /api/v1/auth/logout. Always clears the local token even if the request
 * fails (cookie revocation is best-effort).
 */
export async function logout(): Promise<void> {
  try {
    await apiRequest<void>('/api/v1/auth/logout', {
      method: 'POST',
      skipAuthRefresh: true,
    });
  } catch {
    // Swallow — local state will still be cleared below.
  } finally {
    setAccessToken(null);
  }
}

/** GET /api/v1/auth/me. */
export async function me(): Promise<MeOut> {
  return apiRequest<MeOut>('/api/v1/auth/me', { method: 'GET' });
}

/**
 * POST /api/v1/auth/totp/enroll. Password-gated enrollment start for a
 * not-yet-enrolled user (first login): returns the secret + otpauth URL/QR so
 * the LoginScreen can show it. The user scans it and retries `login()` with a
 * code, which confirms enrolment and logs in. Unauthenticated (no token yet).
 */
export async function enrollTotp(input: {
  email: string;
  password: string;
}): Promise<TotpSetupResponse> {
  return apiRequest<TotpSetupResponse>('/api/v1/auth/totp/enroll', {
    method: 'POST',
    body: JSON.stringify(input),
    skipAuthRefresh: true,
  });
}

/**
 * POST /api/v1/auth/totp/setup. Returns the base32 secret + otpauth URL so
 * the UI can render a QR. `qr_svg` may be null if the backend opts to let the
 * SPA render the QR locally.
 */
export async function totpSetup(): Promise<TotpSetupResponse> {
  return apiRequest<TotpSetupResponse>('/api/v1/auth/totp/setup', {
    method: 'POST',
  });
}

/** POST /api/v1/auth/totp/verify. */
export async function totpVerify(code: string): Promise<void> {
  await apiRequest<void>('/api/v1/auth/totp/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}
